<template>
  <main class="express-admin">
    <section class="panel">
      <!-- 页面顶部标题与操作栏 -->
      <div class="panel-header">
        <div class="header-left">
          <h2>Air&&EXpress 运单与轨迹管理</h2>
        </div>
        <div class="header-right">
          <button class="batch-fetch-btn" :disabled="batchLoading" @click="fetchAll">
            {{ batchLoading ? '正在采集中...' : '⚡ 一键采集全部运单' }}
          </button>
        </div>
      </div>

      <!-- 新增运单卡片表单（5列对齐 Grid 布局） -->
      <div class="create-card">
        <div class="card-title">新增 Air&&EXpress 运单</div>
        <form class="create-grid-form" @submit.prevent="create">
          <div class="form-group">
            <label>系统单号 <span class="req">*</span></label>
            <input v-model.trim="form.systemNo" placeholder="如 AIRE260831001" required>
          </div>
          <div class="form-group">
            <label>承运物流商 <span class="req">*</span></label>
            <select v-model="form.carrier">
              <option value="CHINA_SOUTHERN">南方航空 (CSAir)</option>
              <option value="DHL">DHL 国际快递</option>
              <option value="QIANQIU">钱球 (BaslExpress)</option>
            </select>
          </div>
          <div class="form-group">
            <label>物流商单号 <span class="req">*</span></label>
            <input v-model.trim="form.carrierNo" placeholder="如 784-85040561 / 4839150094" required>
          </div>
          <div class="form-group">
            <label>货物名称</label>
            <input v-model.trim="form.cargoName" placeholder="如 电子配件 / 仪器设备">
          </div>
          <div class="form-group">
            <label>供应商</label>
            <input v-model.trim="form.supplierName" placeholder="如 广州合作仓">
          </div>

          <div class="form-group">
            <label>重量</label>
            <input v-model.trim="form.weight" placeholder="如 545.00">
          </div>
          <div class="form-group">
            <label>体积</label>
            <input v-model.trim="form.volume" placeholder="如 3.20">
          </div>
          <div class="form-group">
            <label>件数</label>
            <input v-model.trim="form.packageQuantity" placeholder="如 12">
          </div>
          <div class="form-group">
            <label>起运地</label>
            <input v-model.trim="form.origin" placeholder="起运地 (如 CAN / SZX / HKG)">
          </div>
          <div class="form-group">
            <label>目的地</label>
            <input v-model.trim="form.destination" placeholder="目的地 (如 SYD / MEL / SIN)">
          </div>

          <!-- 5个里程碑节点可选选择器（跨满整行） -->
          <div class="nodes-select-row full-width-grid">
            <label class="nodes-select-label">选择要展示的运输节点（选了就展示，不选不展示）：</label>
            <div class="nodes-checkboxes">
              <label class="node-cb-item">
                <input type="checkbox" v-model="form.selectedNodes" value="SHIPMENT">
                <span>发货 (Shipment dispatched)</span>
              </label>
              <label class="node-cb-item">
                <input type="checkbox" v-model="form.selectedNodes" value="CUSTOMS">
                <span>报关 (Customs clearance)</span>
              </label>
              <label class="node-cb-item">
                <input type="checkbox" v-model="form.selectedNodes" value="CARRIER_TRACKING">
                <span>官网轨迹 (Carrier trajectory)</span>
              </label>
              <label class="node-cb-item">
                <input type="checkbox" v-model="form.selectedNodes" value="CLEARANCE">
                <span>清关 (Destination clearance)</span>
              </label>
              <label class="node-cb-item">
                <input type="checkbox" v-model="form.selectedNodes" value="DELIVERY">
                <span>派送 (Delivery)</span>
              </label>
            </div>
          </div>

          <div class="form-actions full-width-grid">
            <button class="submit-btn" :disabled="loading">
              {{ loading ? '创建中...' : '+ 新增 Air&&EXpress 运单' }}
            </button>
          </div>
        </form>
      </div>

      <div v-if="error" class="alert-box alert-error">{{ error }}</div>
      <div v-if="successMsg" class="alert-box alert-success">{{ successMsg }}</div>

      <!-- 运单列表表格（排版规整，按钮不折行，日期格式化） -->
      <div class="table-wrap">
        <table class="express-table">
          <thead>
            <tr>
              <th style="width: 140px;">系统单号</th>
              <th style="width: 150px;">承运物流商</th>
              <th style="width: 150px;">物流商单号</th>
              <th style="width: 150px;">路线</th>
              <th>货物规格</th>
              <th style="width: 150px;">更新时间</th>
              <th style="width: 320px; text-align: right;">操作</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="r in rows">
              <tr :key="r.expressId" :class="{ 'row-active': activeSystemNo === r.systemNo || activeTrajectoryNo === r.systemNo }">
                <td>
                  <strong class="system-no-badge">{{ r.systemNo }}</strong>
                </td>
                <td>
                  <span class="carrier-badge">{{ carrierName(r.carrier) }}</span>
                </td>
                <td>
                  <code class="carrier-no-code">{{ r.carrierNo }}</code>
                </td>
                <td>
                  <div class="route-tag">{{ r.origin || 'CAN' }} → {{ r.destination || 'SYD' }}</div>
                </td>
                <td>
                  <div class="cargo-desc">
                    <strong v-if="r.cargoName">{{ r.cargoName }}</strong>
                    <span v-else class="text-muted">未填写品名</span>
                  </div>
                  <div class="cargo-specs">
                    <span v-if="r.weight">重: {{ r.weight }}</span>
                    <span v-if="r.volume">体: {{ r.volume }}CBM</span>
                    <span v-if="r.packageQuantity">件: {{ r.packageQuantity }}</span>
                    <span v-if="r.supplierName">供: {{ r.supplierName }}</span>
                  </div>
                </td>
                <td>
                  <div class="time-cell">{{ formatDateTime(r.updatedAt) }}</div>
                </td>
                <td>
                  <div class="action-btn-group">
                    <button class="btn-fetch" :disabled="fetchingNo === r.systemNo" title="点击人工获取最新官方轨迹" @click="fetchSingle(r.systemNo)">
                      {{ fetchingNo === r.systemNo ? '采集...' : '⚡ 采集' }}
                    </button>
                    <button class="btn-curate" @click="toggleTrajectory(r.systemNo)">
                      📝 实时轨迹明细
                    </button>
                    <button class="btn-detail" @click="toggleDetail(r.systemNo)">
                      ⚙️ 节点设置
                    </button>
                    <button class="btn-delete" title="删除运单" @click="remove(r.systemNo)">
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>

              <!-- 轨迹可视化维护抽屉行（支持点击时间弹窗选择） -->
              <tr v-if="activeTrajectoryNo === r.systemNo" :key="'traj-' + r.expressId" class="expanded-row">
                <td colspan="7">
                  <div class="curate-panel">
                    <div class="curate-header">
                      <div>
                        <h3>【{{ r.systemNo }}】官方轨迹明细维护与审核</h3>
                        <p class="curate-tip">
                          您可以点击上方【⚡ 采集】获取官网数据，或在此直接添加、修改、删除轨迹行。点击保存后即刻在客户端生效。
                        </p>
                      </div>
                      <div class="curate-top-btns">
                        <button class="btn-add-row" @click="addTrajectoryRow">+ 添加一条新轨迹</button>
                      </div>
                    </div>

                    <div v-if="trajectoryLoading" class="loading-box">加载轨迹中...</div>
                    <div v-else>
                      <table class="curate-table">
                        <thead>
                          <tr>
                            <th style="width: 90px; text-align: center;">客户端可见</th>
                            <th style="width: 200px;">发生时间 (点击选择)</th>
                            <th style="width: 170px;">发生地点 (Location)</th>
                            <th style="width: 190px;">状态标题 (Status)</th>
                            <th>详细轨迹说明 (Description)</th>
                            <th style="width: 120px; text-align: center;">操作</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(ev, idx) in curateForm.events" :key="idx" :class="{ 'row-hidden': ev.isVisible === 'N' }">
                            <td style="text-align: center;">
                              <input type="checkbox" v-model="ev.isVisible" true-value="Y" false-value="N" title="勾选则在查询页公开展示，不勾选则对客户隐藏">
                            </td>
                            <td>
                              <el-date-picker
                                v-model="ev.eventTime"
                                type="datetime"
                                format="yyyy-MM-dd HH:mm"
                                value-format="yyyy-MM-dd HH:mm"
                                placeholder="选择发生时间"
                                size="small"
                                style="width: 100%;"
                              ></el-date-picker>
                            </td>
                            <td>
                              <input v-model.trim="ev.location" placeholder="例如 SYDNEY - AUSTRALIA" class="input-cell">
                            </td>
                            <td>
                              <input v-model.trim="ev.statusText" placeholder="例如 Forwarded to delivery agent" class="input-cell">
                            </td>
                            <td>
                              <input v-model.trim="ev.description" placeholder="详细说明..." class="input-cell">
                            </td>
                            <td style="text-align: center;">
                              <button class="btn-move" :disabled="idx === 0" title="上移" @click="moveEventUp(idx)">⬆️</button>
                              <button class="btn-move" :disabled="idx === curateForm.events.length - 1" title="下移" @click="moveEventDown(idx)">⬇️</button>
                              <button class="btn-trash" title="删除此行" @click="deleteTrajectoryRow(idx)">🗑️</button>
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      <div class="curate-footer">
                        <div class="footer-stat">
                          共 <b>{{ curateForm.events.length }}</b> 条事件（公开展示 <b>{{ countVisible(curateForm.events) }}</b> 条）
                        </div>
                        <div class="footer-btns">
                          <button class="btn-cancel" @click="activeTrajectoryNo = ''">取消</button>
                          <button class="btn-save-pub" :disabled="trajectorySaving" @click="saveTrajectory(r.systemNo)">
                            {{ trajectorySaving ? '正在保存发布...' : '💾 保存并立即同步给客户展示' }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

              <!-- 阶段节点维护展开行（支持点击时间弹窗选择，节点标题完整展示） -->
              <tr v-if="activeSystemNo === r.systemNo" :key="'detail-' + r.expressId" class="expanded-row">
                <td colspan="7">
                  <div class="detail-panel">
                    <h3>【{{ r.systemNo }}】5大核心里程碑节点设置（选了展示，不选不展示）</h3>
                    <div v-if="detailLoading">加载节点信息中...</div>
                    <div v-else class="stage-grid">
                      <div v-for="ev in currentEvents" :key="ev.nodeCode" class="stage-card">
                        <div class="stage-name">
                          <label class="enable-node-cb">
                            <input type="checkbox" v-model="ev.isEnabled" true-value="Y" false-value="N">
                            <b>{{ expressNodeNameZh(ev.nodeCode, ev.nodeName || ev.name) }}</b>
                          </label>
                          <span :class="ev.isEnabled === 'N' ? 'tag-disabled' : 'tag-enabled'">
                            {{ ev.isEnabled === 'N' ? '未启用' : '已启用展示' }}
                          </span>
                        </div>
                        <label>
                          <span>节点状态：</span>
                          <select v-model="ev.statusCode">
                            <option value="PENDING">待更新 (PENDING)</option>
                            <option value="PROCESSING">进行中 (PROCESSING)</option>
                            <option value="COMPLETED">已完成 (COMPLETED)</option>
                          </select>
                        </label>
                        <label class="stage-time-label">
                          <span>发生时间：</span>
                          <el-date-picker
                            v-model="ev.occurredAt"
                            type="datetime"
                            format="yyyy-MM-dd HH:mm"
                            value-format="yyyy-MM-dd HH:mm"
                            placeholder="点击选择发生时间"
                            size="small"
                            style="width: 100%;"
                          ></el-date-picker>
                        </label>
                        <label>
                          <span>备注说明：</span>
                          <input v-model.trim="ev.remark" placeholder="备注信息">
                        </label>
                        <button class="btn-save-node" @click="saveEvent(r.systemNo, ev)">保存此节点</button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <p v-if="!rows.length" class="empty-state">暂无 Air&&EXpress 运单记录。</p>
    </section>
  </main>
</template>

<script>
import axios from 'axios'
const api = axios.create({
  timeout: 180000
})

export default {
  name: 'ExpressAdmin',
  data: () => ({
    rows: [],
    error: '',
    successMsg: '',
    loading: false,
    batchLoading: false,
    fetchingNo: '',
    detailLoading: false,
    trajectoryLoading: false,
    trajectorySaving: false,
    activeSystemNo: '',
    activeTrajectoryNo: '',
    currentEvents: [],
    curateForm: {
      isManualLocked: true,
      events: []
    },
    form: {
      systemNo: '',
      carrier: 'CHINA_SOUTHERN',
      carrierNo: '',
      cargoName: '',
      supplierName: '',
      weight: '',
      volume: '',
      packageQuantity: '',
      origin: '',
      destination: '',
      selectedNodes: ['SHIPMENT', 'CUSTOMS', 'CARRIER_TRACKING', 'CLEARANCE', 'DELIVERY']
    }
  }),
  created () {
    api.defaults.headers.common.Authorization = 'Bearer ' + (localStorage.getItem('phoenix_admin_token') || '')
    this.load()
  },
  methods: {
    carrierName (c) {
      return {
        CHINA_SOUTHERN: '南方航空 (CSAir)',
        DHL: 'DHL 国际快递',
        QIANQIU: '钱球 (BaslExpress)'
      }[c] || c
    },
    expressNodeNameZh (code, defaultName) {
      const map = {
        SHIPMENT: '发货 (Shipment dispatched)',
        CUSTOMS: '报关 (Customs clearance)',
        CARRIER_TRACKING: '官网轨迹 (Carrier trajectory)',
        CLEARANCE: '清关 (Destination clearance)',
        DELIVERY: '派送 (Delivery)'
      }
      return defaultName || map[code] || code
    },
    countVisible (events) {
      return (events || []).filter(e => e.isVisible !== 'N').length
    },
    formatDateTime (value) {
      if (!value) return ''
      const s = String(value).replace('T', ' ')
      const m = s.match(/^(\d{4}-\d{2}-\d{2}\s\d{2}:\d{2})/)
      return m ? m[1] : s.substring(0, 16)
    },
    formatDateTimeInput (val) {
      if (!val) return ''
      const s = String(val).trim()
      const m1 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}:\d{2}(?::\d{2})?))?/)
      if (m1) {
        const day = m1[1].padStart(2, '0')
        const month = m1[2].padStart(2, '0')
        const year = m1[3]
        const time = m1[4] ? m1[4].substring(0, 5) : '00:00'
        return `${year}-${month}-${day} ${time}`
      }
      const m2 = s.match(/^(\d{4}-\d{2}-\d{2})[T\s]+(\d{2}:\d{2})/)
      if (m2) {
        return `${m2[1]} ${m2[2]}`
      }
      const m3 = s.match(/^(\d{4}-\d{2}-\d{2})/)
      if (m3) {
        return `${m3[1]} 00:00`
      }
      return s
    },
    async load () {
      this.error = ''
      try {
        const res = await api.get('/api/admin/express')
        this.rows = res.data.records || []
      } catch (e) {
        this.error = e.response?.data?.message || '运单列表加载失败'
      }
    },
    async fetchSingle (systemNo) {
      this.error = ''
      this.successMsg = ''
      this.fetchingNo = systemNo
      try {
        const res = await api.post('/api/admin/express/' + encodeURIComponent(systemNo) + '/fetch')
        if (res.data.success) {
          this.successMsg = `运单 ${systemNo} 采集成功，已同步最新轨迹！`
        } else {
          this.error = `运单 ${systemNo} 采集提示: ` + (res.data.message || '未获取到轨迹')
        }
        await this.load()
        if (this.activeTrajectoryNo === systemNo) {
          await this.loadTrajectory(systemNo)
        }
      } catch (e) {
        this.error = e.response?.data?.message || '采集请求执行异常'
      } finally {
        this.fetchingNo = ''
      }
    },
    async fetchAll () {
      if (!confirm('确定要一键采集全部未完成的 Air&&EXpress 运单吗？')) return
      this.batchLoading = true
      this.error = ''
      this.successMsg = ''
      try {
        const res = await api.post('/api/admin/express/fetch-all?onlyActive=true')
        this.successMsg = `一键采集完成！共处理 ${res.data.total} 票（成功 ${res.data.successCount} 票，异常 ${res.data.failureCount} 票）。`
        await this.load()
      } catch (e) {
        this.error = e.response?.data?.message || '批量采集执行异常'
      } finally {
        this.batchLoading = false
      }
    },
    async create () {
      this.loading = true
      this.error = ''
      this.successMsg = ''
      try {
        await api.post('/api/admin/express', this.form)
        this.successMsg = `运单 ${this.form.systemNo} 创建成功！`
        this.form = {
          systemNo: '',
          carrier: 'CHINA_SOUTHERN',
          carrierNo: '',
          cargoName: '',
          supplierName: '',
          weight: '',
          volume: '',
          packageQuantity: '',
          origin: '',
          destination: '',
          selectedNodes: ['SHIPMENT', 'CUSTOMS', 'CARRIER_TRACKING', 'CLEARANCE', 'DELIVERY']
        }
        await this.load()
      } catch (e) {
        this.error = e.response?.data?.message || '运单创建失败'
      } finally {
        this.loading = false
      }
    },
    async remove (systemNo) {
      if (!confirm(`确定彻底删除 Air&&EXpress 运单【${systemNo}】吗？`)) return
      try {
        await api.delete('/api/admin/express/' + encodeURIComponent(systemNo))
        this.successMsg = `运单 ${systemNo} 已成功删除。`
        await this.load()
      } catch (e) {
        this.error = e.response?.data?.message || '删除运单失败'
      }
    },
    async toggleDetail (systemNo) {
      if (this.activeSystemNo === systemNo) {
        this.activeSystemNo = ''
        return
      }
      this.activeSystemNo = systemNo
      this.activeTrajectoryNo = ''
      this.detailLoading = true
      try {
        const detailRes = await api.get('/api/tracking/' + encodeURIComponent(systemNo))
        this.currentEvents = detailRes.data.events || []
      } catch (e) {
        this.error = '加载节点失败'
      } finally {
        this.detailLoading = false
      }
    },
    async saveEvent (systemNo, ev) {
      try {
        await api.put(`/api/admin/express/${encodeURIComponent(systemNo)}/events/${encodeURIComponent(ev.nodeCode)}`, {
          statusCode: ev.statusCode,
          occurredAt: ev.occurredAt,
          remark: ev.remark,
          isEnabled: ev.isEnabled
        })
        alert(`节点修改已成功保存！`)
        await this.load()
      } catch (e) {
        alert('保存节点失败: ' + (e.response?.data?.message || e.message))
      }
    },
    async toggleTrajectory (systemNo) {
      if (this.activeTrajectoryNo === systemNo) {
        this.activeTrajectoryNo = ''
        return
      }
      this.activeTrajectoryNo = systemNo
      this.activeSystemNo = ''
      await this.loadTrajectory(systemNo)
    },
    async loadTrajectory (systemNo) {
      this.trajectoryLoading = true
      try {
        const res = await api.get(`/api/admin/express/${encodeURIComponent(systemNo)}/trajectory`)
        this.curateForm.isManualLocked = Boolean(res.data.isManualLocked)
        this.curateForm.events = (res.data.events || []).map(e => ({
          eventTime: this.formatDateTimeInput(e.eventTime),
          location: e.location || '',
          statusText: e.statusText || '',
          description: e.description || '',
          isVisible: e.isVisible === 'N' ? 'N' : 'Y'
        }))
      } catch (e) {
        this.error = '加载轨迹数据失败'
      } finally {
        this.trajectoryLoading = false
      }
    },
    addTrajectoryRow () {
      this.curateForm.events.unshift({
        eventTime: new Date().toISOString().replace('T', ' ').substring(0, 16),
        location: '',
        statusText: '',
        description: '',
        isVisible: 'Y'
      })
    },
    deleteTrajectoryRow (idx) {
      this.curateForm.events.splice(idx, 1)
    },
    moveEventUp (idx) {
      if (idx <= 0) return
      const item = this.curateForm.events.splice(idx, 1)[0]
      this.curateForm.events.splice(idx - 1, 0, item)
    },
    moveEventDown (idx) {
      if (idx >= this.curateForm.events.length - 1) return
      const item = this.curateForm.events.splice(idx, 1)[0]
      this.curateForm.events.splice(idx + 1, 0, item)
    },
    async saveTrajectory (systemNo) {
      this.trajectorySaving = true
      try {
        await api.put(`/api/admin/express/${encodeURIComponent(systemNo)}/trajectory`, {
          isManualLocked: false,
          events: this.curateForm.events
        })
        alert(`运单【${systemNo}】的实时轨迹已成功保存并立即发布给客户展示！`)
        await this.load()
      } catch (e) {
        alert('保存轨迹失败: ' + (e.response?.data?.message || e.message))
      } finally {
        this.trajectorySaving = false
      }
    }
  }
}
</script>

<style scoped>
.express-admin {
  padding: 20px 24px;
}
.panel {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 24px;
}
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 16px;
}
.panel-header h2 {
  margin: 0;
  font-size: 20px;
  color: #0f172a;
  font-weight: 800;
}
.batch-fetch-btn {
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  border: none;
  padding: 10px 18px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 700;
  font-size: 13px;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.25);
  white-space: nowrap;
}
.batch-fetch-btn:hover {
  background: linear-gradient(135deg, #059669, #047857);
}
.create-card {
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
}
.card-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 16px;
}
.create-grid-form {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
}
.form-group {
  display: flex;
  flex-direction: column;
}
.full-width-grid {
  grid-column: 1 / -1;
}
.form-group label {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
}
.req {
  color: #ef4444;
}
.form-group input, .form-group select {
  height: 38px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 0 12px;
  font-size: 14px;
  background: #ffffff;
  color: #0f172a;
  box-sizing: border-box;
  width: 100%;
}
.form-group input:focus, .form-group select:focus {
  border-color: #2563eb;
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}
.nodes-select-row {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 14px 18px;
  margin-top: 4px;
}
.nodes-select-label {
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 10px;
  display: block;
}
.nodes-checkboxes {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}
.node-cb-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;
}
.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}
.submit-btn {
  background: #2563eb;
  color: #fff;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
}
.submit-btn:hover {
  background: #1d4ed8;
}
.alert-box {
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 18px;
}
.alert-error {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}
.alert-success {
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
}
.table-wrap {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.express-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.express-table th {
  background: #f1f5f9;
  color: #1e293b;
  font-weight: 700;
  text-align: left;
  padding: 14px 16px;
  border-bottom: 2px solid #cbd5e1;
  white-space: nowrap;
}
.express-table td {
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}
.system-no-badge {
  font-family: Consolas, monospace;
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}
.carrier-badge {
  background: #eff6ff;
  color: #1d4ed8;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid #bfdbfe;
  white-space: nowrap;
}
.carrier-no-code {
  font-family: Consolas, monospace;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  background: #f8fafc;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}
.route-tag {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
}
.cargo-desc strong {
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
  display: block;
}
.cargo-specs {
  display: flex;
  gap: 10px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  margin-top: 3px;
}
.time-cell {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
}
.action-btn-group {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  white-space: nowrap;
}
.btn-fetch, .btn-curate, .btn-detail, .btn-delete {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid #cbd5e1;
  background: #fff;
  white-space: nowrap;
  line-height: 1.2;
}
.btn-fetch {
  background: #f0fdf4;
  border-color: #86efac;
  color: #15803d;
}
.btn-fetch:hover {
  background: #dcfce7;
}
.btn-curate {
  background: #eff6ff;
  border-color: #93c5fd;
  color: #1d4ed8;
}
.btn-curate:hover {
  background: #dbeafe;
}
.btn-detail {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #334155;
}
.btn-detail:hover {
  background: #f1f5f9;
}
.btn-delete {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #dc2626;
  padding: 6px 10px;
}
.btn-delete:hover {
  background: #fee2e2;
}
.expanded-row td {
  background: #f8fafc;
  padding: 20px;
  border-bottom: 2px solid #cbd5e1;
}
.curate-panel, .detail-panel {
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 20px;
}
.curate-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.curate-header h3, .detail-panel h3 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}
.curate-tip {
  margin: 0;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
}
.btn-add-row {
  background: #2563eb;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.curate-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.curate-table th {
  background: #f1f5f9;
  padding: 10px 12px;
  border-bottom: 1px solid #cbd5e1;
  text-align: left;
  font-weight: 700;
  color: #1e293b;
}
.curate-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
}
.input-cell {
  width: 100%;
  height: 34px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 10px;
  font-size: 13px;
  box-sizing: border-box;
  color: #0f172a;
}
.input-cell:focus {
  border-color: #2563eb;
  outline: none;
}
.btn-move, .btn-trash {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid #cbd5e1;
  background: #fff;
  cursor: pointer;
  margin: 0 2px;
}
.btn-trash {
  color: #dc2626;
  border-color: #fca5a5;
  background: #fef2f2;
}
.curate-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #e2e8f0;
}
.footer-stat {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.footer-btns {
  display: flex;
  gap: 12px;
}
.btn-cancel {
  background: #fff;
  border: 1px solid #cbd5e1;
  padding: 8px 18px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-save-pub {
  background: #10b981;
  color: #fff;
  border: none;
  padding: 8px 22px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
}
.stage-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-top: 16px;
}
.stage-card {
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 16px;
}
.stage-name {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.enable-node-cb {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.enable-node-cb b {
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
}
.tag-enabled {
  font-size: 11px;
  color: #15803d;
  background: #dcfce7;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 700;
}
.tag-disabled {
  font-size: 11px;
  color: #64748b;
  background: #e2e8f0;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}
.stage-card label {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}
.stage-card input, .stage-card select {
  height: 32px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
  margin-top: 4px;
  color: #0f172a;
  background: #fff;
}
.btn-save-node {
  width: 100%;
  background: #2563eb;
  color: #fff;
  border: none;
  padding: 7px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 6px;
}
.empty-state {
  text-align: center;
  padding: 36px;
  color: #64748b;
  font-size: 14px;
}
</style>
